import { useCallback, useRef, useState } from 'react';
import {
  FileCheckStatus,
  PromptCheckStatus,
  StagedContextFile,
  SubmitAiCreateResponse,
  SubmitFileResult,
  TargetLength,
  useSubmitAiCreateMutation,
} from '@/modules/services/aiCreate';
import { uploadContextFileToCkfinder } from '@/lib/util/ckfinderUpload';
import config from '@/lib/config';

/** Identity of a file in the view state, so a message can be hung off it. */
export const fileKey = (file: File): string =>
  `${file.name}:${file.size}:${file.lastModified}`;

export interface PromptValidationResult {
  /** The exact (cleaned) prompt text this result belongs to. */
  text: string;
  status: PromptCheckStatus;
}

/**
 * The API's verdict, or `accepted` — a `not_relevant` verdict the admin overrode by
 * clicking Create again (LMS2-5098). Stored server-side (accepted_at); mirrored here so
 * the row shows no message and is not offered for override again.
 */
export type FileValidationStatus = FileCheckStatus | 'accepted';

/** A verdict that lets Create go ahead for this file. */
/**
 * How bad a verdict is, for a document picked twice: the slots are judged separately
 * and can disagree, and the row must show the one that actually stops the request.
 * A hard failure outranks a relevance flag, which outranks a pass.
 */
const severity = (status: FileValidationStatus): number => {
  if (status === 'ok' || status === 'accepted') return 0;
  if (status === 'not_relevant') return 1;
  return 2;
};
export type FileValidationResults = Record<string, FileValidationStatus>;

/**
 * Hangs each verdict on the file it is about. Joined on slot: the file at slot i is the
 * one sent at position i. Not on name (two uploads can share one, and cobra rewrites it)
 * and not on the response order.
 */
const toFileStatuses = (
  results: SubmitFileResult[],
  files: File[],
): FileValidationResults => {
  const received: FileValidationResults = {};
  results.forEach(({ slot, status, accepted }) => {
    const file = files[slot];
    if (!file) return;
    // An overridden relevance flag reads as usable, not as a verdict to show.
    const shown: FileValidationStatus =
      status === 'not_relevant' && accepted ? 'accepted' : status;
    // The same document picked twice shares one key, so the worse verdict wins:
    // showing a relevance flag over a block would leave Create enabled for a
    // click the server is going to reject.
    const key = fileKey(file);
    const previous = received[key];
    received[key] =
      previous && severity(previous) > severity(shown) ? previous : shown;
  });
  return received;
};

/** What the modal needs to render the current validation state. */
export interface AiValidationView {
  isValidating: boolean;
  promptResult: PromptValidationResult | null;
  fileStatuses: FileValidationResults;
  /** Quota / system failure for the whole click. */
  submitError: string | null;
  /** 'quota' renders under the prompt field (wireframe B7); 'system' near Create. */
  submitErrorKind: 'quota' | 'system' | null;
}

export type SubmitOutcome = 'started' | 'rejected' | 'error';

export interface SubmitArgs {
  name: string;
  prompt: string;
  targetLength: TargetLength;
  files: File[];
  /**
   * Files whose `not_relevant` flag the admin is overriding with this click (LMS2-5098:
   * the second Create click is the "use it anyway"). Sent as `accepted: true` on their
   * slot; the server records it and stops judging them.
   */
  acceptFiles?: File[];
}

export interface SubmitResult {
  outcome: SubmitOutcome;
  /** Set on 'started' — the id the progress modal polls. */
  requestId: string | null;
}

// Copy from the Create with AI wireframes (LMS2-5095 "Visual context", frame B7).
export const AI_QUOTA_EXHAUSTED_MESSAGE =
  'Your organisation has used all its AI actions for this month. You can still build this module from scratch, or ask your account owner about more AI actions.';
export const GENERIC_VALIDATION_ERROR =
  'We couldn’t check your prompt and files. Please try again.';
export const UPLOAD_FAILED_MESSAGE =
  'We could not upload your files. Please try again.';

export const TENANT_NOT_READY_MESSAGE =
  'Still getting things ready. Please try again in a moment.';

export const AI_BUSY_MESSAGE =
  'Your organisation already has the maximum number of modules generating. Please wait for one to finish and try again.';
/**
 * Shown with the field message when the worker sent the request back over a file
 * (LMS2-5141), so the admin knows the progress screen closed on purpose and nothing ran.
 */
export const FILE_VERDICT_NOTE =
  "We stopped before creating your module because one of your files can't be used. Change the file and try again.";
export const UPLOAD_TOO_LARGE_MESSAGE =
  'Your context files are too large to upload together. Keep the total under 30MB.';

const toSubmitError = (
  err: any,
): { message: string; kind: 'quota' | 'system' } => {
  if (err?.data?.error === 'MONTHLY_CAP_EXCEEDED') {
    return { message: AI_QUOTA_EXHAUSTED_MESSAGE, kind: 'quota' };
  }
  // Reachable on any Create click now, not just the one that starts a generation:
  // the merged endpoint checks the concurrency limit before it checks anything else.
  // Without this the admin is shown the raw "Concurrent AI request limit reached: 3/3".
  if (err?.data?.error === 'AI_CONCURRENCY_LIMIT') {
    return { message: AI_BUSY_MESSAGE, kind: 'system' };
  }
  if (err?.status === 413) {
    return { message: UPLOAD_TOO_LARGE_MESSAGE, kind: 'system' };
  }
  const raw = err?.data?.message;
  return {
    message:
      typeof raw === 'string' && raw.length > 0
        ? raw
        : GENERIC_VALIDATION_ERROR,
    kind: 'system',
  };
};

/**
 * Owns the Create-with-AI submit for one open setup modal (LMS2-5164).
 *
 * One click, one request: the API checks the whole current form state and, if
 * everything passes, the generation is already queued by the time this resolves.
 * There is no separate start call and no second loading state.
 *
 * There is also no re-validation cache here any more. The server keeps the draft
 * and decides what changed — the prompt is re-screened only when its text moved,
 * a file only when its bytes did — so the FE's job is to send the current form
 * state and render the verdicts that come back. What it does keep is `requestId`,
 * which is how the server recognises the next click as the same attempt.
 *
 * Scope is the modal lifetime: the parent calls `reset()` on close and on a card
 * switch. `reset()` also aborts any in-flight request, invalidates its result and
 * drops the requestId, so a late response can never repopulate state that was just
 * cleared and the next click starts a fresh draft.
 */
/**
 * @param tenantId Needed to stage context files. Passed in rather than read from the
 * tenant-profile query so this hook keeps no service dependency of its own and can be
 * tested without a redux Provider; the caller already holds it for the PPT path.
 */
export const useAiCreateSubmit = (tenantId?: string) => {
  const [submit, { isLoading }] = useSubmitAiCreateMutation();
  const [promptResult, setPromptResult] =
    useState<PromptValidationResult | null>(null);
  const [fileStatuses, setFileStatuses] = useState<FileValidationResults>({});
  // Staged object per file, so a file crosses the wire once rather than once per click.
  // Keyed on the file's identity, not its slot: the modal keeps a dense array, so
  // removing the first of two shifts the survivor and a slot-keyed cache would re-upload
  // a document that never changed.
  // Keyed by file identity, holding the upload in flight rather than its result: the
  // same document picked twice starts both uploads in the same tick, and only a
  // shared promise lets the second slot wait on the first instead of sending the
  // bytes again (and ending up with two different staged objects for one file).
  const stagedRef = useRef<Record<string, Promise<string>>>({});
  // Uploading is part of the click as far as the admin is concerned, so it has to drive
  // the same one loading state; otherwise Create looks idle while files are going up.
  const [isStaging, setIsStaging] = useState(false);

  const [submitError, setSubmitError] = useState<{
    message: string;
    kind: 'quota' | 'system';
  } | null>(null);
  // The draft the server is holding for this modal session. Sent on every click
  // after the first so the server reconciles instead of re-checking everything.
  const requestIdRef = useRef<string | null>(null);
  // In-flight mutation (for abort) and a generation counter: a run whose epoch no
  // longer matches after `await` belongs to a modal state that has since been reset.
  const inFlightRef = useRef<{ abort: () => void } | null>(null);
  const epochRef = useRef(0);

  /**
   * Uploads any file we have not staged yet and returns the references to send.
   *
   * Slot is the file's position in the modal's array, which is what the per-field
   * messages join back on.
   */
  const stageFiles = useCallback(
    async (
      files: File[],
      acceptKeys: Set<string> = new Set(),
    ): Promise<StagedContextFile[]> => {
      if (files.length > 0 && !tenantId) {
        throw new Error(TENANT_NOT_READY_MESSAGE);
      }

      return Promise.all(
        files.map(async (file, slot) => {
          const key = fileKey(file);
          const accepted = acceptKeys.has(key) ? { accepted: true } : {};
          let staged = stagedRef.current[key];
          if (!staged) {
            staged = uploadContextFileToCkfinder(file, {
              tenantId: tenantId as string,
              connectorPath: config.ckFinderConnectorPath,
            }).then(({ s3Key }) => s3Key);
            stagedRef.current[key] = staged;
            // A failed upload must not poison the key: the next click retries it.
            staged.catch(() => {
              if (stagedRef.current[key] === staged) {
                delete stagedRef.current[key];
              }
            });
          }
          const s3Key = await staged;
          return { slot, s3Key, name: file.name, ...accepted };
        }),
      );
    },
    [tenantId],
  );

  const run = useCallback(
    async ({
      name,
      prompt,
      targetLength,
      files,
      acceptFiles = [],
    }: SubmitArgs): Promise<SubmitResult> => {
      setSubmitError(null);

      const epoch = epochRef.current;

      // Stage anything not already in S3. Only new files upload: on a second click after
      // a prompt edit this loop does nothing at all, which is the point of sending
      // references instead of re-posting the documents.
      let staged: StagedContextFile[];
      setIsStaging(true);
      try {
        staged = await stageFiles(files, new Set(acceptFiles.map(fileKey)));
      } catch (err: any) {
        if (epoch !== epochRef.current) {
          return { outcome: 'error', requestId: null };
        }
        setSubmitError({
          message:
            typeof err?.message === 'string'
              ? err.message
              : UPLOAD_FAILED_MESSAGE,
          kind: 'system',
        });
        return { outcome: 'error', requestId: null };
      } finally {
        setIsStaging(false);
      }

      // The upload itself cannot be aborted, but a reset while it was running means this
      // click belongs to a modal the admin has already left. Submitting now would queue a
      // generation nobody is watching.
      if (epoch !== epochRef.current) {
        return { outcome: 'error', requestId: null };
      }

      let result: SubmitAiCreateResponse;
      try {
        const request = submit({
          ...(requestIdRef.current ? { requestId: requestIdRef.current } : {}),
          name,
          prompt,
          targetLength,
          files: staged,
        });
        inFlightRef.current = request;
        result = await request.unwrap();
        inFlightRef.current = null;
      } catch (err: any) {
        inFlightRef.current = null;
        if (epoch !== epochRef.current || err?.name === 'AbortError') {
          return { outcome: 'error', requestId: null };
        }
        // A failure after the server created the draft carries its id. Keep it, or the
        // retry the admin is about to make starts a fresh draft and re-runs every check
        // they already waited for — and a 503 from a checker is exactly when they retry.
        if (typeof err?.data?.requestId === 'string') {
          requestIdRef.current = err.data.requestId;
        }
        setSubmitError(toSubmitError(err));
        return { outcome: 'error', requestId: null };
      }

      // Reset happened while we were waiting: this result belongs to a cleared
      // modal state. Drop it silently.
      if (epoch !== epochRef.current) {
        return { outcome: 'error', requestId: null };
      }

      requestIdRef.current = result.requestId;

      if (result.prompt) {
        setPromptResult({ text: prompt, status: result.prompt.status });
      }
      // Replace rather than merge: the response answers for every live file, so a
      // leftover entry could only belong to a file that is no longer in the form.
      setFileStatuses(toFileStatuses(result.files, files));

      // A rejection the form itself cannot explain: every field came back fine and the
      // tenant is simply busy. Without this the admin sees a form with no errors and a
      // Create button that appears to do nothing.
      if (result.blockedBy === 'concurrency') {
        setSubmitError({ message: AI_BUSY_MESSAGE, kind: 'system' });
      }

      return {
        outcome: result.outcome,
        requestId: result.outcome === 'started' ? result.requestId : null,
      };
    },
    [submit, stageFiles],
  );

  /**
   * Clears a removed file's message. The server forgets the slot on the next
   * submit; this is what stops a stale message from keeping Create disabled in the
   * meantime. `too_much_text` on the other file goes too: it can be about the pair,
   * which the removal may have fixed, so the next click re-decides it (LMS2-5216).
   */
  const forgetFile = useCallback((file: File) => {
    // The note is about the file the admin is now changing.
    setSubmitError((prev) =>
      prev?.message === FILE_VERDICT_NOTE ? null : prev,
    );
    setFileStatuses((prev) => {
      const key = fileKey(file);
      const next = Object.fromEntries(
        Object.entries(prev).filter(
          ([other, status]) => other !== key && status !== 'too_much_text',
        ),
      );
      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  }, []);

  /**
   * The worker sent the request back to its draft over a file only it could judge
   * (LMS2-5141). The form comes back with that verdict on the file, and the next click
   * continues the same draft: the server reuses every verdict it holds, so an unchanged
   * form is answered at once and only a replaced file is checked again. The staged
   * uploads are kept — the server reads its own copy of a file it has already stored.
   */
  const restoreDraft = useCallback(
    (requestId: string, results: SubmitFileResult[], files: File[]) => {
      requestIdRef.current = requestId;
      setFileStatuses(toFileStatuses(results, files));
      setSubmitError({ message: FILE_VERDICT_NOTE, kind: 'system' });
    },
    [],
  );

  const reset = useCallback(() => {
    epochRef.current += 1;
    inFlightRef.current?.abort();
    inFlightRef.current = null;
    requestIdRef.current = null;
    // The draft is gone, so the objects staged against it are no use to the next attempt.
    stagedRef.current = {};
    setPromptResult(null);
    setFileStatuses({});
    setSubmitError(null);
  }, []);

  const view: AiValidationView = {
    isValidating: isLoading || isStaging,
    promptResult,
    fileStatuses,
    submitError: submitError?.message ?? null,
    submitErrorKind: submitError?.kind ?? null,
  };

  return { run, forgetFile, restoreDraft, reset, view };
};
